// Per-clip settings dialog: rename, gain, fades, and one-click processing
// (normalize, reverse, clean voice, reduce noise, analyze tempo).
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { el, gainToDb } from "../util";
import { openModal, toast } from "./modal";
import {
  clipPeak, linToDb, normalizeClip, reverseClip, cleanVoiceClip, reduceNoiseClip, analyzeTempo,
  makeMonoClip, trimSilenceClip, stretchToTempoClip, clipQuietestMono, reduceNoiseProfileClip,
} from "../audio/clipfx";
import { separateStems, webgpuAvailable } from "../audio/separate";
import { getNoiseProfile, setNoiseProfile, buildNoiseProfile, captureNoiseFromMic } from "../audio/noise-reduction";
import { newTrack, newClip } from "../state/model";

export function openClipInspector(store: Store, engine: AudioEngine, clipId: string) {
  const found = store.findClip(clipId);
  if (!found) return;
  const content = el("div", { class: "fx-editor" });
  let busy = false;
  let nrStrength = 0.7;

  // --- noise reduction: sample a profile (mic or clip), then subtract it ---
  const sampleNoiseFromMic = async () => {
    if (busy) return; busy = true;
    toast("Capturing ~1.5 s of ambient noise — stay quiet…", 2500);
    try {
      const mono = await captureNoiseFromMic(engine.ctx, { seconds: 1.5 });
      setNoiseProfile(buildNoiseProfile(mono, engine.ctx.sampleRate, "live mic"));
      engine.syncGraph(); // push the new profile to any live Denoise inserts
      toast("Noise profile captured from mic.");
    } catch (e: any) { console.error(e); toast("Mic capture failed: " + (e?.message || e), 6000); }
    busy = false; render();
  };
  const sampleNoiseFromClip = () => {
    if (busy) return;
    const f = store.findClip(clipId); if (!f) return;
    const buf = store.buffers.get(f.clip.assetId);
    const mono = clipQuietestMono(store, f.clip);
    if (!mono || !buf) { toast("No audio to sample."); return; }
    setNoiseProfile(buildNoiseProfile(mono, buf.sampleRate, "this clip's quiet part"));
    engine.syncGraph(); // push the new profile to any live Denoise inserts
    toast("Noise profile captured from this clip's quietest part.");
    render();
  };
  const applyNoiseProfile = () => {
    const prof = getNoiseProfile();
    if (!prof) { toast("Sample a noise profile first — from the mic or a quiet part of a clip."); return; }
    run("Reduce noise", (c) => reduceNoiseProfileClip(store, engine, c, prof.mag, nrStrength));
  };

  // The action targets are all selected clips (so actions apply to the whole
  // selection), falling back to the clip the inspector was opened on.
  const targets = () => {
    const list = store.selectedClipList().map((x) => x.clip);
    if (list.length) return list;
    const f = store.findClip(clipId);
    return f ? [f.clip] : [];
  };

  // Run a per-clip operation across every target clip as one batch.
  const run = async (label: string, perClip: (clip: any) => any) => {
    if (busy) return;
    const clips = targets();
    if (!clips.length) return;
    busy = true; toast(`${label}${clips.length > 1 ? ` (${clips.length} clips)` : ""}…`, 4000);
    try { for (const c of clips) await perClip(c); toast(`${label} done.`); }
    catch (e) { console.error(e); toast(`${label} failed.`); }
    busy = false; render();
  };

  function render() {
    const f = store.findClip(clipId);
    if (!f) { content.innerHTML = ""; return; }
    const clip = f.clip;
    content.innerHTML = "";

    // name
    const name = el("input", { attrs: { value: clip.name } }) as HTMLInputElement;
    name.value = clip.name;
    name.addEventListener("change", () => store.updateClip(clipId, { name: name.value }));
    content.append(row("Name", name));

    // gain
    const peak = clipPeak(store, clip);
    const gain = slider(-24, 24, 0.5, gainToDb(clip.gain));
    const gOut = el("output", { text: fmtDb(gainToDb(clip.gain)) });
    // value edits apply to the whole selection (or just this clip if it's the only one)
    const applyAll = (patch: any) => { if (store.selectedClipIds.size > 1) store.updateSelectedClips(patch, false); else store.updateClip(clipId, patch, false); };
    gain.addEventListener("input", () => {
      const db = Number(gain.value); gOut.textContent = fmtDb(db);
      applyAll({ gain: db <= -24 ? 0 : Math.pow(10, db / 20) });
    });
    content.append(row("Gain", gain, gOut));
    content.append(el("p", { class: "hint", text: `Region peak: ${peak > 0 ? fmtDb(linToDb(peak)) : "—"}` }));

    // fades
    const fi = slider(0, 2, 0.01, clip.fadeIn);
    const fiOut = el("output", { text: clip.fadeIn.toFixed(2) + "s" });
    fi.addEventListener("input", () => { fiOut.textContent = Number(fi.value).toFixed(2) + "s"; applyAll({ fadeIn: Number(fi.value) }); });
    content.append(row("Fade in", fi, fiOut));
    const fo = slider(0, 2, 0.01, clip.fadeOut);
    const foOut = el("output", { text: clip.fadeOut.toFixed(2) + "s" });
    fo.addEventListener("input", () => { foOut.textContent = Number(fo.value).toFixed(2) + "s"; applyAll({ fadeOut: Number(fo.value) }); });
    content.append(row("Fade out", fo, foOut));

    // actions
    const actions = el("div", { class: "clip-actions" });
    actions.append(
      actionBtn("Normalize", () => run("Normalize", (c) => { normalizeClip(store, c); })),
      actionBtn("Reverse", () => run("Reverse", (c) => reverseClip(store, engine, c))),
      actionBtn("Make mono", () => run("Make mono", (c) => makeMonoClip(store, engine, c))),
      actionBtn("Trim silence", () => run("Trim silence", (c) => { trimSilenceClip(store, c); })),
      actionBtn("Clean voice", () => run("Clean voice", (c) => cleanVoiceClip(store, engine, c))),
      actionBtn("Reduce noise", () => run("Reduce noise", (c) => reduceNoiseClip(store, engine, c))),
    );
    content.append(el("div", { class: "hint", text: "Processing (creates a new take; undoable):" }), actions);

    // Profile-based spectral noise reduction — grouped in one bordered panel so
    // it reads as a single 3-step effect process (sample → set strength → apply).
    const prof = getNoiseProfile();
    const nrStatus = el("span", {
      class: prof ? "badge live" : "badge",
      text: prof ? `Profile ready: ${prof.source}` : "No profile yet",
    });
    const strength = slider(0, 1, 0.05, nrStrength);
    const sOut = el("output", { text: `${Math.round(nrStrength * 100)}%` });
    strength.addEventListener("input", () => { nrStrength = Number(strength.value); sOut.textContent = `${Math.round(nrStrength * 100)}%`; });
    const applyBtn = actionBtn("Reduce noise", applyNoiseProfile);
    applyBtn.classList.add("nr-apply");
    if (!prof) applyBtn.setAttribute("disabled", "true");

    const nrPanel = el("div", { class: "nr-panel" }, [
      el("div", { class: "nr-head" }, [
        el("span", { class: "nr-title", text: "🔇 Profile Noise Reduction" }),
        nrStatus,
      ]),
      el("p", { class: "hint", text: "Removes steady background noise (hiss/hum) by sampling it, then subtracting that fingerprint from the recording." }),
      el("div", { class: "nr-step" }, [
        el("span", { class: "nr-num", text: "1" }),
        el("div", { class: "nr-body" }, [
          el("div", { class: "nr-label", text: "Sample a noise profile (a noise-only snippet)" }),
          el("div", { class: "clip-actions" }, [
            actionBtn("🎙 From mic", sampleNoiseFromMic),
            actionBtn("📋 From this clip", sampleNoiseFromClip),
          ]),
        ]),
      ]),
      el("div", { class: "nr-step" }, [
        el("span", { class: "nr-num", text: "2" }),
        el("div", { class: "nr-body" }, [
          el("div", { class: "nr-label", text: "Set strength" }),
          el("div", { class: "fx-param" }, [strength, sOut]),
        ]),
      ]),
      el("div", { class: "nr-step" }, [
        el("span", { class: "nr-num", text: "3" }),
        el("div", { class: "nr-body" }, [
          el("div", { class: "nr-label", text: "Apply to the selected clip(s)" }),
          el("div", { class: "clip-actions" }, [applyBtn]),
        ]),
      ]),
    ]);
    content.append(nrPanel);

    // true ML stem separation (vocals + instrumental → two new tracks)
    const sepBtn = actionBtn("✂ Separate stems (beta)", () => separate(clip));
    content.append(el("div", { class: "hint", text: "True separation (ML) — creates Vocals + Instrumental tracks:" }),
      el("div", { class: "clip-actions" }, [sepBtn]),
      el("p", { class: "hint", text: webgpuAvailable() ? "First run downloads a ~78 MB model (cached). Runs on your GPU (Spleeter). Vocals are band-limited to ~11 kHz." : "No WebGPU here — will run on CPU (slow); Chrome/Edge desktop recommended." }));

    // analyze tempo
    const tempoBtn = actionBtn("Analyze tempo", () => {
      const bpm = analyzeTempo(store, clip);
      if (!bpm) { toast("Couldn't detect a tempo."); return; }
      tempoOut.textContent = `~${bpm} BPM`;
      applyTempo.style.display = "";
      applyTempo.onclick = () => { store.project.tempo = bpm; store.emitChange("project"); toast(`Project tempo set to ${bpm} BPM.`); };
    });
    const tempoOut = el("span", { class: "badge", text: "—" });
    const applyTempo = el("button", { class: "mini", text: "Set project tempo" });
    applyTempo.style.display = "none"; applyTempo.style.width = "auto";
    const matchBtn = actionBtn("Match project tempo", () => run("Time-stretch", (c) => stretchToTempoClip(store, engine, c)));
    content.append(el("div", { class: "clip-actions" }, [tempoBtn, tempoOut, applyTempo, matchBtn]));
  }

  async function separate(clip: any) {
    if (busy) return;
    const buf = store.buffers.get(clip.assetId);
    if (!buf) { toast("No audio to separate."); return; }
    // extract the clip's audible region at the current rate
    const sr = engine.ctx.sampleRate;
    const start = Math.max(0, Math.floor(clip.offset * sr));
    const len = Math.min(buf.length - start, Math.floor(clip.duration * sr));
    const region = engine.ctx.createBuffer(buf.numberOfChannels, Math.max(1, len), sr);
    for (let ch = 0; ch < buf.numberOfChannels; ch++) region.getChannelData(ch).set(buf.getChannelData(ch).subarray(start, start + len));

    busy = true;
    const status = el("p", { class: "hint", text: "Loading model…" });
    const bar = el("div", { class: "progbar" }, [el("i")]);
    const fill = bar.firstChild as HTMLElement;
    const body = el("div", {}, [status, bar, el("p", { class: "hint", text: "Keep this tab focused; this can take a while." })]);
    const close = openModal("Separating stems (beta)", body, []);
    try {
      const { vocals, instrumental, provider } = await separateStems(engine, region, (phase, frac) => {
        status.textContent = phase === "download" ? `Downloading model… ${Math.round(frac * 100)}%` : `Separating… ${Math.round(frac * 100)}%`;
        fill.style.width = `${Math.round(frac * 100)}%`;
      });
      const i = store.project.tracks.length;
      const vt = newTrack(i); vt.name = "Vocals"; store.addTrack(vt);
      const it = newTrack(i + 1); it.name = "Instrumental"; store.addTrack(it);
      const va = store.addAsset(vocals, `${clip.name} (vocals)`);
      const ia = store.addAsset(instrumental, `${clip.name} (instrumental)`);
      store.addClip(vt.id, newClip(va.id, va, clip.start));
      store.addClip(it.id, newClip(ia.id, ia, clip.start));
      close();
      toast(`Separated → Vocals + Instrumental tracks (${provider}).`, 6000);
    } catch (e: any) {
      console.error("separation failed", e);
      close();
      toast("Separation failed: " + (e?.message || e), 8000);
    } finally { busy = false; }
  }

  render();
  const n = store.selectedClipIds.size;
  openModal(n > 1 ? `Clip settings — ${n} clips` : "Clip settings", content, [{ label: "Done", primary: true, onClick: () => {} }]);
}

function row(label: string, ...controls: HTMLElement[]): HTMLElement {
  return el("div", { class: "fx-param" }, [el("label", { text: label }), ...controls]);
}
function slider(min: number, max: number, step: number, value: number): HTMLInputElement {
  return el("input", { attrs: { type: "range", min: String(min), max: String(max), step: String(step), value: String(value) } }) as HTMLInputElement;
}
function actionBtn(label: string, on: () => void): HTMLButtonElement {
  const b = el("button", { text: label }); b.addEventListener("click", on); return b;
}
function fmtDb(db: number): string { return db === -Infinity ? "-∞ dB" : `${db >= 0 ? "+" : ""}${db.toFixed(1)} dB`; }
